import { useState, useEffect } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { Search, Globe, Share2, Save, BarChart3, ExternalLink } from "lucide-react";

const SEOSettingsPage = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seoSettings, setSeoSettings] = useState({
    site_title: "",
    meta_description: "",
    meta_keywords: "",
    og_title: "",
    og_description: "",
    og_image: "",
    twitter_title: "",
    twitter_description: "",
    twitter_image: "",
    canonical_url: "",
    google_analytics_id: "",
    google_tag_manager_id: ""
  });

  useEffect(() => {
    fetchSEOSettings();
  }, []);

  const fetchSEOSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/seo-settings`);
      setSeoSettings(res.data);
    } catch (err) {
      console.error("Failed to fetch SEO settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/admin/seo-settings?admin_id=${user.id}`, seoSettings);
      toast.success("SEO settings updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update SEO settings");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSeoSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">SEO Settings</h2>
        <p className="text-muted-foreground">
          Manage search engine optimization settings for BizilGO.com
        </p>
      </div>

      {/* Basic SEO */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-blue-500" />
            Basic SEO
          </CardTitle>
          <CardDescription>
            Core meta tags that help search engines understand your website
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="site-title">Site Title</Label>
            <Input
              id="site-title"
              placeholder="BizilGO - Bike Taxi App"
              value={seoSettings.site_title}
              onChange={(e) => updateField("site_title", e.target.value)}
              data-testid="seo-site-title"
            />
            <p className="text-xs text-muted-foreground">Appears in browser tabs and search results</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="meta-description">Meta Description</Label>
            <Textarea
              id="meta-description"
              placeholder="Fast, affordable bike & auto rides at your fingertips..."
              value={seoSettings.meta_description}
              onChange={(e) => updateField("meta_description", e.target.value)}
              rows={3}
              data-testid="seo-meta-description"
            />
            <p className="text-xs text-muted-foreground">150-160 characters recommended for search results</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="meta-keywords">Meta Keywords</Label>
            <Input
              id="meta-keywords"
              placeholder="bike taxi, auto booking, ride sharing, Hyderabad..."
              value={seoSettings.meta_keywords}
              onChange={(e) => updateField("meta_keywords", e.target.value)}
              data-testid="seo-meta-keywords"
            />
            <p className="text-xs text-muted-foreground">Comma-separated keywords relevant to your business</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="canonical-url">Canonical URL</Label>
            <Input
              id="canonical-url"
              placeholder="https://bizilgo.com"
              value={seoSettings.canonical_url}
              onChange={(e) => updateField("canonical_url", e.target.value)}
              data-testid="seo-canonical-url"
            />
            <p className="text-xs text-muted-foreground">The preferred URL for this page</p>
          </div>
        </CardContent>
      </Card>

      {/* Open Graph (Facebook, LinkedIn) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-blue-600" />
            Open Graph (Social Sharing)
          </CardTitle>
          <CardDescription>
            Controls how your site appears when shared on Facebook, LinkedIn, etc.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="og-title">OG Title</Label>
            <Input
              id="og-title"
              placeholder="BizilGO - Bike Taxi App"
              value={seoSettings.og_title}
              onChange={(e) => updateField("og_title", e.target.value)}
              data-testid="seo-og-title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="og-description">OG Description</Label>
            <Textarea
              id="og-description"
              placeholder="Fast, affordable bike & auto rides..."
              value={seoSettings.og_description}
              onChange={(e) => updateField("og_description", e.target.value)}
              rows={2}
              data-testid="seo-og-description"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="og-image">OG Image URL</Label>
            <Input
              id="og-image"
              placeholder="https://bizilgo.com/icons/icon-512x512.png"
              value={seoSettings.og_image}
              onChange={(e) => updateField("og_image", e.target.value)}
              data-testid="seo-og-image"
            />
            <p className="text-xs text-muted-foreground">Recommended size: 1200x630 pixels</p>
          </div>
        </CardContent>
      </Card>

      {/* Twitter Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-sky-500" />
            Twitter Card
          </CardTitle>
          <CardDescription>
            Controls how your site appears when shared on Twitter/X
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="twitter-title">Twitter Title</Label>
            <Input
              id="twitter-title"
              placeholder="BizilGO - Bike Taxi App"
              value={seoSettings.twitter_title}
              onChange={(e) => updateField("twitter_title", e.target.value)}
              data-testid="seo-twitter-title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="twitter-description">Twitter Description</Label>
            <Textarea
              id="twitter-description"
              placeholder="Fast, affordable bike & auto rides..."
              value={seoSettings.twitter_description}
              onChange={(e) => updateField("twitter_description", e.target.value)}
              rows={2}
              data-testid="seo-twitter-description"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="twitter-image">Twitter Image URL</Label>
            <Input
              id="twitter-image"
              placeholder="https://bizilgo.com/icons/icon-512x512.png"
              value={seoSettings.twitter_image}
              onChange={(e) => updateField("twitter_image", e.target.value)}
              data-testid="seo-twitter-image"
            />
          </div>
        </CardContent>
      </Card>

      {/* Analytics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-green-500" />
            Analytics & Tracking
          </CardTitle>
          <CardDescription>
            Configure Google Analytics and Tag Manager for tracking
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ga-id">Google Analytics ID</Label>
            <Input
              id="ga-id"
              placeholder="G-XXXXXXXXXX"
              value={seoSettings.google_analytics_id}
              onChange={(e) => updateField("google_analytics_id", e.target.value)}
              data-testid="seo-ga-id"
            />
            <p className="text-xs text-muted-foreground">
              Your GA4 Measurement ID (starts with G-)
              {seoSettings.google_analytics_id && (
                <a 
                  href={`https://analytics.google.com/analytics/web/#/p${seoSettings.google_analytics_id.replace('G-', '')}/reports/intelligenthome`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  Open Analytics
                </a>
              )}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gtm-id">Google Tag Manager ID (Optional)</Label>
            <Input
              id="gtm-id"
              placeholder="GTM-XXXXXXX"
              value={seoSettings.google_tag_manager_id}
              onChange={(e) => updateField("google_tag_manager_id", e.target.value)}
              data-testid="seo-gtm-id"
            />
            <p className="text-xs text-muted-foreground">Optional: For advanced tag management</p>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-seo-btn">
        {saving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            Saving...
          </>
        ) : (
          <>
            <Save className="w-4 h-4 mr-2" />
            Save SEO Settings
          </>
        )}
      </Button>
    </div>
  );
};

export default SEOSettingsPage;
